import { useState } from "react";
import { Html } from "@react-three/drei";
import { OverlayCanvas } from "./OverlayCanvas";
import { EditorBuildingInfo } from "@/components/Editor/EditorBuildingInfo";
import { EditorActionButtons } from "@/components/Editor/EditorActionButtons";

// 에디터 박스 타입
interface EditorBox {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

interface BottomLeftOverlayProps {
  /** 에디터 props */
  editorProps?: {
    gridX: number;
    gridZ: number;
    boxes: EditorBox[];
    onClose: () => void;
    onBuildingCreated?: () => void;
  };
}

// 빈티지 문서 스타일 정의
const VINTAGE_STYLES = {
  container: {
    width: "240px",
    padding: "20px 16px",
    background: "linear-gradient(180deg, #d4c4a8 0%, #c9b896 100%)",
    borderRadius: "4px",
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    color: "#2a2a2a",
    boxShadow: `
      0 10px 30px rgba(0,0,0,0.3),
      inset 0 1px 0 rgba(255,255,255,0.3),
      inset 0 -2px 0 rgba(0,0,0,0.1)
    `,
    border: "2px solid #8b7355",
  },
  paper: {
    background: "linear-gradient(180deg, #faf8f0 0%, #f5f0e1 100%)",
    padding: "16px 14px",
    borderRadius: "3px",
    boxShadow: "inset 0 0 15px rgba(0,0,0,0.05)",
    border: "1px solid #d4c9b0",
  },
  header: {
    fontSize: "15px",
    fontWeight: 700,
    marginBottom: "16px",
    paddingBottom: "10px",
    borderBottom: "2px solid #8b7355",
    fontFamily: "'Noto Serif KR', 'Georgia', serif",
    textAlign: "center" as const,
    letterSpacing: "1px",
    color: "#3d3d3d",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  gridBadge: {
    fontSize: "10px",
    padding: "2px 6px",
    background: "rgba(139, 115, 85, 0.1)",
    color: "#8b7355",
    borderRadius: "2px",
    fontWeight: 600,
  },
};

/**
 * BottomLeft 오버레이 컴포넌트 - 빈티지 문서 스타일
 * 빈 셀 클릭 시 표시 - 건물 정보 입력 + 액션 버튼
 */
export function BottomLeftOverlay({ editorProps }: BottomLeftOverlayProps) {
  // 에디터 모드 상태
  const [buildingName, setBuildingName] = useState("");
  const [buildingText, setBuildingText] = useState("");

  return (
    <OverlayCanvas corner="bottomLeft" zoom={50}>
      {/* editorProps가 있을 때만 내용 렌더링 */}
      {editorProps && (
        <Html
          position={[-3.5, -3.5, 0]}
          rotation={[-Math.PI / 2, 0, Math.PI / 2]}
          transform
          occlude={false}
          style={{ pointerEvents: "auto" }}
        >
          <div style={VINTAGE_STYLES.container}>
            {/* 종이 */}
            <div style={VINTAGE_STYLES.paper}>
              {/* 헤더 */}
              <div style={VINTAGE_STYLES.header}>
                <span>건물 등록 신청서</span>
              </div>

              {/* 건물 정보 입력 */}
              <EditorBuildingInfo
                buildingName={buildingName}
                setBuildingName={setBuildingName}
                buildingText={buildingText}
                setBuildingText={setBuildingText}
              />

              {/* 액션 버튼 */}
              <div
                className="mt-6 pt-2"
                style={{ borderTop: "1px dashed #d4c9b0" }}
              >
                <EditorActionButtons
                  boxes={editorProps.boxes}
                  buildingName={buildingName}
                  buildingText={buildingText}
                  gridX={editorProps.gridX}
                  gridZ={editorProps.gridZ}
                  onClose={editorProps.onClose}
                  onBuildingCreated={editorProps.onBuildingCreated}
                />
              </div>
            </div>
          </div>
        </Html>
      )}
    </OverlayCanvas>
  );
}

export default BottomLeftOverlay;
